// Pure helpers for the paginated Suitability Questionnaire. Kept framework-free so they can
// be unit tested without mounting the Vue components.

const EMPTY_ADDRESS = { street1: "", street2: "", city: "", state: "", zip_code: "" };

/**
 * Combine a structured address into a single freeform string:
 *   "street1, street2, city, state zip".
 * Empty parts are dropped so the result never has dangling separators.
 */
export function serializeAddress(address = {}) {
  const trim = (v) => (v || "").trim();
  const line1 = [address.street1, address.street2].map(trim).filter(Boolean).join(", ");
  const region = [address.state, address.zip_code].map(trim).filter(Boolean).join(" ");
  const cityRegion = [trim(address.city), region].filter(Boolean).join(", ");
  return [line1, cityRegion].filter(Boolean).join(", ");
}

/**
 * Best-effort inverse of serializeAddress for previously stored freeform strings.
 * Only splits when it can confidently find "…, City, ST ZIP" at the end; otherwise keeps the
 * whole string in street1 so no data is lost. Always returns a full address shape.
 */
export function parseAddress(str) {
  if (!str || typeof str !== "string") return { ...EMPTY_ADDRESS };
  const parts = str
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
  const last = parts[parts.length - 1] || "";
  const zipMatch = last.match(/(\d{5}(?:-\d{4})?)\s*$/);
  if (parts.length >= 3 && zipMatch) {
    return {
      street1: parts[0],
      street2: parts.slice(1, parts.length - 2).join(", "),
      city: parts[parts.length - 2],
      state: last.slice(0, zipMatch.index).trim(),
      zip_code: zipMatch[1],
    };
  }
  return { ...EMPTY_ADDRESS, street1: str };
}

/**
 * Partition a flat list of questionnaire field objects into their display groups.
 * `groups` is the ordered SUITABILITY_QUESTIONNAIRE_GROUPS config. Fields not claimed by any
 * questionnaire group fall into the LAST questionnaire group so nothing is ever dropped when
 * the backend schema adds a field. Non-questionnaire groups (e.g. the Form-rendered financial
 * group) get an empty `schema`.
 */
export function partitionQuestionnaireGroups(allFields = [], groups = []) {
  const claimed = new Set(groups.flatMap((g) => g.fields ?? []));
  const questionGroups = groups.filter((g) => g.renderer === "questionnaire");
  const lastQuestionGroup = questionGroups[questionGroups.length - 1];

  return groups.map((g) => {
    if (g.renderer !== "questionnaire") return { ...g, schema: [] };
    let schema = (g.fields ?? [])
      .map((fn) => allFields.find((f) => f.field_name === fn))
      .filter(Boolean);
    if (g === lastQuestionGroup) {
      schema = [...schema, ...allFields.filter((f) => !claimed.has(f.field_name))];
    }
    return { ...g, schema };
  });
}
